import React, { useState, useReducer, useEffect, useContext } from "react";
import Select from "react-select";
import { NotificationManager } from "react-notifications";
import Papa from "papaparse";
import useApi from "../Api/Api";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faFile,
  faCheck,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../Components/Button";
import CsvDrop from "../Components/CsvDrop";
import { FilterContext } from "../FilterContextProvider";

const UploadProduct = ({
  handleShowSelf,
  handleShowManualForm,
  handleShowUploadForm,
  afterSubmit,
  fromArtistPage,
  fromHome,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [submitIsDone, setSubmitIsDone] = useState(false);
  const [frontEndError, setFrontEndError] = useState(false);
  const [fileWithError, setFileWithError] = useState(false);
  const history = useHistory();
  const api = useApi();
  const { filterData, setFilterData } = useContext(FilterContext);
  const [artists, setArtists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);

  const artistOptions = artists.map((artist) => ({
    label: artist.name,
    value: artist.id,
  }));

  const handleArtistSelect = (option) => {
    setFilterData({
      ...filterData,
      artist_id: option.value,
      artist_name: option.label,
    });
  };

  const initialState = { dropDepth: 0, inDropZone: false, fileList: [] };

  const reducer = (state, action) => {
    switch (action.type) {
      case "SET_DROP_DEPTH":
        return { ...state, dropDepth: action.dropDepth };
      case "SET_IN_DROP_ZONE":
        return { ...state, inDropZone: action.inDropZone };
      case "ADD_FILE_TO_LIST":
        return { ...state, fileList: state.fileList.concat(action.files) };
      case "REMOVE_FILE_FROM_LIST":
        const newFiles = [...state.fileList];
        newFiles.splice(action["i"], 1);
        return { ...state, fileList: newFiles };
      default:
        return state;
    }
  };

  const [data, dispatch] = useReducer(reducer, initialState);
  const [productsToAdd, setProductsToAdd] = useState([]);

  const removeFile = (i) => {
    dispatch({ type: "REMOVE_FILE_FROM_LIST", i });
  };

  useEffect(() => {
    const products = [];
    data.fileList.forEach((arr, i) => {
      Papa.parse(arr, {
        header: true,
        download: true,
        complete: function (results) {
          const removeDuplicates = results.data.filter(
            (data, index, self) =>
              index ===
              self.findIndex(
                (d) => d.name === data.name && d.size === data.size
              )
          );
          if (results.data.length - removeDuplicates.length !== 0) {
            setFrontEndError(
              `There are ${
                results.data.length - removeDuplicates.length
              } products with identical name and size. Please edit the file and try again. None of the products in this file will be added.`
            );
            setFileWithError(i);
          } else if (
            results.data.some((obj) => obj.name === "") ||
            results.data.some((obj) => obj.retail_price === "") ||
            results.data.some((obj) => obj.cost === "") ||
            results.data.some((obj) => obj.category === "") ||
            results.data[0].hasOwnProperty("name") === false ||
            results.data[0].hasOwnProperty("retail_price") === false ||
            results.data[0].hasOwnProperty("cost") === false ||
            results.data[0].hasOwnProperty("category") === false
          ) {
            setFrontEndError(
              `At least one product in this file does not have a name, retail price, cost, or category; or the file is missing a header row with those titles. Please edit the file and try again. Download the CSV Template above for an example of the required format. None of the products in this file will be added.`
            );
            setFileWithError(i);
            data.fileList[
              i
            ].error = `At least one product in this file does not have a name, retail price, cost, or category; or the file is missing a header row with those titles. Please edit the file and try again. Download the CSV Template above for an example of the required format. None of the products in this file will be added.`;
          } else {
            setFrontEndError(false);
            setFileWithError(false);
            products.push(results.data);
            setProductsToAdd([...products]);
          }
        },
      });
    });
  }, [data, filterData.artist_id]);

  const commonSizes = {
    y: "youth",
    "x-s": "xs",
    "x-small": "xs",
    xsmall: "xs",
    sm: "s",
    small: "s",
    md: "m",
    med: "m",
    medium: "m",
    lg: "l",
    large: "l",
    "x-l": "xl",
    "x-large": "xl",
    xlarge: "xl",
    "xx-l": "xxl",
    "xx-large": "xxl",
    xxlarge: "xxl",
    "xxx-l": "xxxl",
    "xxx-large": "xxxl",
    xxxlarge: "xxxl",
    "xxxx-l": "xxxxl",
    "xxxx-large": "xxxxl",
    xxxxlarge: "xxxxl",
  };

  const addSizeAndCategoryIds = () => {
    productsToAdd.forEach((arr) => {
      if (arr.length !== 0) {
        arr.forEach((each) => {
          if (commonSizes[each.size.toLowerCase()]) {
            each.size = commonSizes[each.size.toLowerCase()];
          }
          const sizeCopy = sizes.find(
            ({ name }) => each.size.toLowerCase() === name.toLowerCase()
          );
          const categoryCopy = categories.find(
            ({ name }) => each.category.toLowerCase() === name.toLowerCase()
          );
          if (sizeCopy?.id) {
            each.size_id = sizeCopy.id;
          }
          if (categoryCopy?.id) {
            each.category_id = categoryCopy.id;
          }
          if (!each.size_id) {
            each.size_other = each.size;
          }
        });
      }
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage();
    setSubmitIsDone(false);
    setIsFetching(true);
    addSizeAndCategoryIds();
    Promise.all(
      productsToAdd.map((arr, i) => {
        return api
          .put(`/artists/${filterData.artist_id}`, {
            artist: {
              products_attributes: [
                ...arr.map((each) => ({
                  name: each.name,
                  category_id: each.category_id,
                  size_id: each.size_id,
                  retail_price: each.retail_price,
                  cost: each.cost,
                  size_other: each.size_other,
                  artist_id: filterData.artist_id,
                })),
              ],
            },
          })
          .then(() => {
            NotificationManager.success(
              "Products successfully added",
              "Success",
              2000
            );
          })
          .catch((error) => {
            const err = Object.keys(error.response.data.errors).map((key) => {
              const parts = key.split(".");
              const rowNum = parseInt(parts[0].match(/\d+/)) + 1;
              const type = parts[1];
              return `row ${rowNum}: ${type} ${error.response.data.errors[key][0]}`;
            });
            return { [data.fileList[i].name]: err };
          });
      })
    )
      .then((results) => {
        setSubmitIsDone(true);
        const errors = results.reduce((a, e) => (e ? { ...a, ...e } : a), {});
        setErrorMessage(errors);
        setIsFetching(false);
        if (!Object.keys(errors).length) {
          history.push("/inventory");
          handleShowUploadForm();
          afterSubmit();
        }
      })
      .finally(() => {});
    event.preventDefault();
  };

  useEffect(() => {
    api.get(`/artists`).then((r) => {
      setArtists(r.data);
    });
    api.get(`/categories`).then((r) => {
      setCategories(r.data);
    });
    api.get(`/sizes`).then((r) => {
      setSizes(r.data);
    });
  }, [api]);

  return (
    <div className="-mt-20 side-modal">
      <div className="flex-col">
        <header className="py-6 px-4 text-lg mb-2 bg-blue-500 text-white">
          <button onClick={handleShowUploadForm} className="float-right -mt-4">
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </button>
          {fromArtistPage && (
            <div className="-mt-4">
              <button
                onClick={handleShowSelf}
                className="-pt-5 text-xs tracking-wide"
              >
                <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to{" "}
                <span className="italics">add artist</span>
              </button>
            </div>
          )}
          <div>
            Import Product for
            {filterData.artist_name && (
              <span className="font-bold"> {filterData.artist_name}</span>
            )}
          </div>
          <div className="text-sm">
            To revert back to manual entry, click{" "}
            <button
              onClick={handleShowManualForm}
              className="font-bold underline"
            >
              here
            </button>
          </div>
        </header>
        <div className="mt-6 px-4 text-lg text-black">Upload Files</div>
        <form className="mx-4 my-8 text-sm">
          {fromHome && (
            <>
              <label htmlFor="artist_id">Artist</label>
              <Select
                id="artist_id"
                placeholder="Select an artist"
                className="text-xs font-light mt-1"
                options={artistOptions}
                value={artistOptions?.filter(
                  (each) => each.value === filterData.artist_id
                )}
                onChange={(option) => handleArtistSelect(option)}
              />
            </>
          )}
          <CsvDrop data={data} dispatch={dispatch} />
        </form>
        <div className="mx-4 my-8 text-sm">
          <a
            className="text-blue-500"
            href="/artist_merch_template.csv"
            download
          >
            Download CSV Template
          </a>
          <div className="my-8 ml-2 font-light text-xs italic">
            Uploaded files
          </div>
          <div className="mx-6">
            {data.fileList.map((file, i) => {
              return (
                <div className="mt-6" key={i}>
                  <FontAwesomeIcon
                    className="mr-6 text-xl"
                    icon={faFile}
                  ></FontAwesomeIcon>
                  {file.name}
                  {i === fileWithError ? (
                    <>
                      <button
                        className="float-right text-red-600 -mr-2"
                        onClick={() => removeFile(i)}
                      >
                        <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                      </button>
                      <div className="text-red-500">{frontEndError}</div>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        className="fixed right-8 text-green-600 mr-8"
                        icon={faCheck}
                      ></FontAwesomeIcon>
                      <button
                        className="float-right text-red-600 -mr-2"
                        onClick={() => removeFile(i)}
                      >
                        <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-center mt-8">
            {errorMessage &&
              submitIsDone &&
              Object.keys(errorMessage).map((fileName, i) => (
                <div key={i} className="text-red-500 pb-4">
                  <div>{fileName}</div>
                  {errorMessage[fileName].map((each, j) => (
                    <div key={j}>{each}</div>
                  ))}
                </div>
              ))}
          </div>
          <div className="flex justify-end mt-8 float-right">
            <Button
              text={isFetching ? `Loading ${"\u00A0"}` : "Done adding"}
              onClick={handleSubmit}
              disabled={isFetching || !productsToAdd.length}
              inProgress={isFetching}
              className={
                productsToAdd.length === 0 ? "button-disabled" : "button"
              }
            ></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadProduct;
